import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkApiConfig } from 'core/store';
import {
  ActiveOrderProject,
  AggiornaCostiRendicontazioniRequest,
  CdgProjectOtherCostsRequest,
  CdgProjectOtherCostsUpdateRequest,
  ClAssociateOrderProjectRequest,
  ClProjectOtherCostsRequest,
  ClProjectOtherCostsUpdateRequest,
  ClSearchOrdersSFDetailRequest,
  CloseEconomicsProjectRequest,
  CompletedProjectRequest,
  Configuration,
  ContractOrderOutputDto,
  ContractOrderOutputListDto,
  ContractOrderSFSearchInputDto,
  CreateContractOrderAttivoDto,
  ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi,
  ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi,
  ControllerPerLeFunzioniDedicateAlPlApi,
  FilterProjectMonthDto,
  FilterProjectMonthValuesDto,
  OrderSFSearchFilterDto,
  OtherCostListDto,
  ProjectMonthEditRequest,
  ProjectMonthListDto,
  ProjectMonthSearchRequest,
} from '@api/client';
import getConfiguration, { errorHandler } from 'core/store/config';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';

enum ManagementToolsActions {
  getCdgFilters = '[CDG] Get Filters',
  editCdgProjects = '[CDG] Edit Projects',
  getCdgProjects = '[CDG] Get Projects',
  saveCdgFiltersUsed = '[CDG] Save Filters Used',
  getCdgOtherCosts = '[CDG] Get Other Costs',
  saveCdgOtherCosts = '[CDG] Save Other Costs',
  refreshHrCosts = '[CDG] Refresh Hr Costs',
  completeProjectCdg = '[CDG] Complete Project',
  getClFilters = '[CL] Get Filters',
  editClProjects = '[CL] Edit Projects',
  getClProjects = '[CL] Get Projects',
  saveClFiltersUsed = '[CL] Save Filters Used',
  getClOtherCosts = '[CL] Get Other Costs',
  saveClOtherCosts = '[CL] Save Other Costs',
  closeEconomicsCl = '[CL] Close Economics',
  /* CL Orders */
  searchClOrders = '[CL] Search Orders',
  searchClOrderSfDetail = '[CL] Search Order SF Detail',
  createClOrder = '[CL] Create Order',
  deleteClOrder = '[CL] Delete Order',
  getClOrderOptions = '[CL] Get Order Options',
  saveClFiltersOrdersUsed = '[CL] Save Filters Orders Used',
  clAssociateOrderProject = '[CL] Associate Order Project',
  resetClOrderDetail = '[CL] Reset Order Detail',
  getPlFilters = '[PL] Get Filters',
  editPlProjects = '[PL] Edit Projects',
  getPlProjects = '[PL] Get Projects',
  savePlFiltersUsed = '[PL] Save Filters Used',
  getPlOtherCosts = '[PL] Get Other Costs',
}

/* #################### CDG #################### */

export const getCdgFilters = createAsyncThunk<FilterProjectMonthValuesDto | undefined, void, ThunkApiConfig>(
  ManagementToolsActions.getCdgFilters,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
        getConfiguration(Configuration, extra, getState)
      ).projectMonthSearchFilters();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const editCdgProjects = createAsyncThunk<string | undefined, ProjectMonthEditRequest, ThunkApiConfig>(
  ManagementToolsActions.editCdgProjects,
  async (payload, { dispatch, extra, getState }) => {
    try {
      const result = await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
        getConfiguration(Configuration, extra, getState)
      ).projectMonthEdit(payload);
      AppToastService.success('Projects updated successfully');
      return result;
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getCdgProjects = createAsyncThunk<
  ProjectMonthListDto | undefined,
  ProjectMonthSearchRequest,
  ThunkApiConfig
>(ManagementToolsActions.getCdgProjects, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
      getConfiguration(Configuration, extra, getState)
    ).projectMonthSearch(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getCdgOtherCosts = createAsyncThunk<
  OtherCostListDto | undefined,
  CdgProjectOtherCostsRequest,
  ThunkApiConfig
>(ManagementToolsActions.getCdgOtherCosts, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
      getConfiguration(Configuration, extra, getState)
    ).cdgProjectOtherCosts(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const saveCdgOtherCosts = createAsyncThunk<
  OtherCostListDto | undefined,
  CdgProjectOtherCostsUpdateRequest,
  ThunkApiConfig
>(ManagementToolsActions.saveCdgOtherCosts, async (payload, { dispatch, extra, getState }) => {
  try {
    const result = await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
      getConfiguration(Configuration, extra, getState)
    ).cdgProjectOtherCostsUpdate(payload);
    AppToastService.success('Other costs saved successfully');
    return result;
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const refreshHrCosts = createAsyncThunk<string | undefined, AggiornaCostiRendicontazioniRequest, ThunkApiConfig>(
  ManagementToolsActions.refreshHrCosts,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
        getConfiguration(Configuration, extra, getState)
      ).aggiornaCostiRendicontazioni(payload);
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const completeProjectCdg = createAsyncThunk<string | undefined, CompletedProjectRequest, ThunkApiConfig>(
  ManagementToolsActions.completeProjectCdg,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDiGestioneApi(
        getConfiguration(Configuration, extra, getState)
      ).completedProject(payload);
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

/* #################### CL #################### */

export const getClFilters = createAsyncThunk<FilterProjectMonthValuesDto | undefined, void, ThunkApiConfig>(
  ManagementToolsActions.getClFilters,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
        getConfiguration(Configuration, extra, getState)
      ).clProjectMonthSearchFilters();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const editClProjects = createAsyncThunk<string | undefined, ProjectMonthEditRequest, ThunkApiConfig>(
  ManagementToolsActions.editClProjects,
  async (payload, { dispatch, extra, getState }) => {
    try {
      const result = await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
        getConfiguration(Configuration, extra, getState)
      ).clProjectMonthEdit(payload);
      AppToastService.success('Projects updated successfully');
      return result;
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getClProjects = createAsyncThunk<
  ProjectMonthListDto | undefined,
  ProjectMonthSearchRequest,
  ThunkApiConfig
>(ManagementToolsActions.getClProjects, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clProjectMonthSearch(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getClOtherCosts = createAsyncThunk<
  OtherCostListDto | undefined,
  ClProjectOtherCostsRequest,
  ThunkApiConfig
>(ManagementToolsActions.getClOtherCosts, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clProjectOtherCosts(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const saveClOtherCosts = createAsyncThunk<
  OtherCostListDto | undefined,
  ClProjectOtherCostsUpdateRequest,
  ThunkApiConfig
>(ManagementToolsActions.saveClOtherCosts, async (payload, { dispatch, extra, getState }) => {
  try {
    const result = await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clProjectOtherCostsUpdate(payload);
    AppToastService.success('Other costs saved successfully');
    return result;
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const closeEconomicsCl = createAsyncThunk<string | undefined, CloseEconomicsProjectRequest, ThunkApiConfig>(
  ManagementToolsActions.closeEconomicsCl,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
        getConfiguration(Configuration, extra, getState)
      ).closeEconomicsProject(payload);
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

/* #################### CL ORDERS #################### */

export const searchClOrders = createAsyncThunk<
  ContractOrderOutputListDto | undefined,
  { searchDto: ContractOrderSFSearchInputDto },
  ThunkApiConfig
>(ManagementToolsActions.searchClOrders, async ({ searchDto }, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clSearchOrdersSF({ contractOrderSFSearchInputDto: searchDto });
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const createClOrder = createAsyncThunk<ContractOrderOutputDto | undefined, CreateContractOrderAttivoDto, ThunkApiConfig>(
  ManagementToolsActions.createClOrder,
  async (payload, { dispatch, extra, getState }) => {
    try {
      const result = await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
        getConfiguration(Configuration, extra, getState)
      ).clCreateOrder({ createContractOrderAttivoDto: payload });
      AppToastService.success('Order created successfully');
      return result;
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const deleteClOrder = createAsyncThunk<number | undefined, number, ThunkApiConfig>(
  ManagementToolsActions.deleteClOrder,
  async (id, { dispatch, getState }) => {
    // TODO: Replace with actual API call when backend is ready
    // try {
    //   await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
    //     getConfiguration(Configuration, extra, getState)
    //   ).deleteClOrder({ orderId: id });
    //   return id;
    // } catch (e) {
    //   errorHandler(e, dispatch, getState);
    // }
    AppToastService.success('Order deleted successfully');
    return id;
  }
);

export const getClOrderOptions = createAsyncThunk<OrderSFSearchFilterDto | undefined, void, ThunkApiConfig>(
  ManagementToolsActions.getClOrderOptions,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
        getConfiguration(Configuration, extra, getState)
      ).clGetOrderSearchFilters();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const saveClFiltersOrdersUsed = createAction<ContractOrderSFSearchInputDto>(ManagementToolsActions.saveClFiltersOrdersUsed);

export const searchClOrderSfDetail = createAsyncThunk<
  ContractOrderOutputListDto | undefined,
  ClSearchOrdersSFDetailRequest,
  ThunkApiConfig
>(ManagementToolsActions.searchClOrderSfDetail, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clSearchOrdersSFDetail(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const clAssociateOrderProject = createAsyncThunk<
  ActiveOrderProject | undefined,
  ClAssociateOrderProjectRequest,
  ThunkApiConfig
>(ManagementToolsActions.clAssociateOrderProject, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlControlloDelResponsabileDiContrattoApi(
      getConfiguration(Configuration, extra, getState)
    ).clAssociateOrderProject(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const resetClOrderDetail = createAction(ManagementToolsActions.resetClOrderDetail);

/* #################### PL #################### */

export const getPlFilters = createAsyncThunk<FilterProjectMonthValuesDto | undefined, void, ThunkApiConfig>(
  ManagementToolsActions.getPlFilters,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new ControllerPerLeFunzioniDedicateAlPlApi(
        getConfiguration(Configuration, extra, getState)
      ).plProjectMonthSearchFilters();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const editPlProjects = createAsyncThunk<string | undefined, ProjectMonthEditRequest, ThunkApiConfig>(
  ManagementToolsActions.editPlProjects,
  async (payload, { dispatch, extra, getState }) => {
    try {
      const result = await new ControllerPerLeFunzioniDedicateAlPlApi(
        getConfiguration(Configuration, extra, getState)
      ).plProjectMonthEdit(payload);
      AppToastService.success('Projects updated successfully');
      return result;
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getPlProjects = createAsyncThunk<
  ProjectMonthListDto | undefined,
  ProjectMonthSearchRequest,
  ThunkApiConfig
>(ManagementToolsActions.getPlProjects, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlPlApi(
      getConfiguration(Configuration, extra, getState)
    ).plProjectMonthSearch(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getPlOtherCosts = createAsyncThunk<
  OtherCostListDto | undefined,
  ClProjectOtherCostsRequest,
  ThunkApiConfig
>(ManagementToolsActions.getPlOtherCosts, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerPerLeFunzioniDedicateAlPlApi(
      getConfiguration(Configuration, extra, getState)
    ).plProjectOtherCosts(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const saveCdgFiltersUsed = createAction<FilterProjectMonthDto>(ManagementToolsActions.saveCdgFiltersUsed);
export const saveClFiltersUsed = createAction<FilterProjectMonthDto>(ManagementToolsActions.saveClFiltersUsed);
export const savePlFiltersUsed = createAction<FilterProjectMonthDto>(ManagementToolsActions.savePlFiltersUsed);
